import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useFormState } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InternalIdInput, JsonEditor } from '@library'
import { ActionsConfigTab } from '@mobileComponents/EventFlow'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { SaveDraftDialog } from '@mobileComponents/SaveDraftDialog'
import { TabPanel } from '@mobileComponents/TabPanel'
import { VariablesConfigTab } from '@mobileComponents/Variables'
import { DEFAULT_MOBILE_FORM } from '@mobileConstants'
import { extractServerErrorMessage, validateMobileForm } from '@mobileHelpers'
import { useDraft, useMobileForms } from '@mobileHooks'
import { MobileFormPreview } from '@mobilePreviews'
import { MobileForm } from '@mobileTypes'
import { Grid, Paper, Tab, Tabs, TextField } from '@mui/material'

import { ROUTES } from '@constants'

import { FormElementsConfigTab } from './components/FormElementsConfigTab'

export const MobileFormCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!id

  const { createForm, updateForm, getFormById } = useMobileForms()
  const { saveDraft, clearDraft, draft, hasDraft } = useDraft<MobileForm>(
    `mobile-form-draft-${id ?? 'new'}`
  )
  const shouldAutoRestore = useRef((location.state as any)?.restoreDraft === true)
  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)

  const methods = useForm<MobileForm>({
    defaultValues: isEdit ? getFormById(id) || DEFAULT_MOBILE_FORM : DEFAULT_MOBILE_FORM,
  })

  const { control, handleSubmit, reset, watch } = methods

  const formData = watch()
  const { isDirty } = useFormState({ control })

  useEffect(() => {
    if (!isEdit && shouldAutoRestore.current && draft) {
      Object.keys(draft.data).forEach(key => {
        methods.setValue(key as any, (draft.data as any)[key], {
          shouldDirty: true,
          shouldValidate: false,
        })
      })
      setJsonValue(JSON.stringify(draft.data, null, 2))
      shouldAutoRestore.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && id) {
      const form = getFormById(id)
      if (form) {
        if (shouldAutoRestore.current && draft) {
          Object.keys(draft.data).forEach(key => {
            methods.setValue(key as any, (draft.data as any)[key], {
              shouldDirty: true,
              shouldValidate: false,
            })
          })
          setJsonValue(JSON.stringify(draft.data, null, 2))
          shouldAutoRestore.current = false
        } else {
          reset(form)
          setJsonValue(JSON.stringify(form, null, 2))
        }
      }
    }
  }, [id, isEdit, getFormById, reset])

  useEffect(() => {
    if (tabValue === 4) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [formData, tabValue])

  const [errors, setErrors] = useState<string[]>([])

  const onSubmit = async (data: MobileForm) => {
    const validationErrors = validateMobileForm(data)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    try {
      if (isEdit && id) {
        const currentForm = getFormById(id)
        if (currentForm) {
          const updatedForm = await updateForm(id, data)
          reset(updatedForm)
          setJsonValue(JSON.stringify(updatedForm, null, 2))
          clearDraft()
        }
      } else {
        const newForm = await createForm(data)
        clearDraft()
        navigate(`${ROUTES.CONFIG_MOBILE_FORMS_EDIT}/${newForm.id}`, { replace: true })
      }
    } catch (error: unknown) {
      setSaveError(extractServerErrorMessage(error))
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_FORMS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const form = getFormById(id)
      if (form) {
        reset(form)
        setJsonValue(JSON.stringify(form, null, 2))
        setErrors([])
      }
    } else {
      reset(DEFAULT_MOBILE_FORM)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_FORM, null, 2))
      setErrors([])
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use setValue for each field to mark form as dirty
      Object.keys(parsed).forEach(key => {
        methods.setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch (error) {
      setJsonValue(newValue)
    }
  }

  const previewComponent = useMemo(() => {
    if (tabValue === 1) {
      return <MobileFormPreview form={formData} />
    }

    return undefined
  }, [tabValue, formData])

  return (
    <>
      <SaveDraftDialog
        open={!!saveError}
        errorMessage={saveError ?? ''}
        onSaveDraft={() => saveDraft(methods.getValues())}
        onClose={() => setSaveError(null)}
      />
      <MobileConfiguratorLayout
        title={isEdit ? 'Edit Mobile Form' : 'Create Mobile Form'}
        isEdit={isEdit}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onReset={handleReset}
        isDirty={isDirty}
        errors={errors}
        previewComponent={previewComponent}
        draftProps={{
          hasDraft,
          draftSavedAt: draft?.savedAt,
          onRestoreDraft: () => {
            if (draft) {
              methods.reset(draft.data, { keepDefaultValues: true })
              setJsonValue(JSON.stringify(draft.data, null, 2))
            }
          },
          onDiscardDraft: clearDraft,
          onSaveDraft: () => saveDraft(methods.getValues()),
        }}
      >
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label='Basic Info' />
            <Tab label='Config' />
            <Tab label='Variables' />
            <Tab label='Actions' />
            <Tab label='JSON Editor' />
          </Tabs>
        </Paper>

        <FormProvider {...methods}>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name='internalId'
                  control={control}
                  rules={{ required: 'Internal ID is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <InternalIdInput
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      fullWidth
                      label='Internal ID'
                      disabled={isEdit}
                      error={!!error}
                      helperText={error?.message}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name='title'
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='Form Title'
                      error={!!error}
                      helperText={error?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <FormElementsConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <VariablesConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ActionsConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <JsonEditor
              value={jsonValue}
              onChange={handleJsonChange}
              label='Form Configuration (JSON)'
              minHeight={400}
            />
          </TabPanel>
        </FormProvider>
      </MobileConfiguratorLayout>
    </>
  )
}
